import '../css/PosAdmin.css';

/**
 * One client's staff: an address per row, with the role that account holds.
 *
 * CONTROLLED, and the array is the source of truth — the parent holds the
 * rows and gets a whole new array on every change. Rows are keyed by INDEX
 * deliberately: an address is not an id, it is being typed, and two rows can
 * legitimately hold the same text mid-edit, which a value-based key would
 * collapse into one.
 *
 * An empty list renders one empty row rather than nothing, so there is always
 * somewhere to type; blank rows are dropped on save.
 *
 * @param {object} props
 * @param {Array<{email: string, roleId: string}>} props.values
 * @param {(values: Array<{email: string, roleId: string}>) => void} props.onChange
 * @param {Array<{id: string, name: string}>} props.roles - the client's roles,
 *   as the Roles tab currently has them. Unsaved roles appear here too, which
 *   is deliberate: an administrator should be able to define a role and assign
 *   it in one sitting.
 * @param {string} [props.placeholder] - suggested address format.
 * @param {boolean} [props.disabled]
 * @returns {JSX.Element}
 */
function PosStaffList({ values, onChange, roles = [], placeholder = '', disabled = false }) {
  const rows = values.length > 0 ? values : [{ email: '', roleId: '' }];

  /**
   * Replaces one field of one row.
   *
   * @param {number} index
   * @param {'email'|'roleId'} field
   * @returns {(event: React.ChangeEvent<HTMLInputElement|HTMLSelectElement>) => void}
   */
  const handleChange = (index, field) => (event) => {
    const next = [...rows];
    next[index] = { ...next[index], [field]: event.target.value };
    onChange(next);
  };

  /**
   * Removes one row, leaving a single empty row rather than an empty list.
   *
   * @param {number} index
   * @returns {void}
   */
  const handleRemove = (index) => {
    const next = rows.filter((_, i) => i !== index);
    onChange(next.length > 0 ? next : [{ email: '', roleId: '' }]);
  };

  /** Appends an empty row for the next account. @returns {void} */
  const handleAdd = () => onChange([...rows, { email: '', roleId: '' }]);

  return (
    <div className="pa-list">
      {rows.map((row, index) => {
        // A role assigned and then deleted from the Roles tab would otherwise
        // select nothing and look unassigned, so the dead id is offered back
        // as an option that names the problem. Saving is refused until it is
        // changed (see savePosClientStaff).
        const missingRole = row.roleId && !roles.some((role) => role.id === row.roleId);
        return (
          // eslint-disable-next-line react/no-array-index-key
          <div className="pa-staff-row" key={index}>
            <input
              className="pa-input pa-staff-email"
              type="email"
              value={row.email}
              onChange={handleChange(index, 'email')}
              placeholder={placeholder}
              disabled={disabled}
              spellCheck="false"
              aria-label={`staff address ${index + 1}`}
            />

            <select
              className={`pa-input pa-staff-role${missingRole ? ' pa-staff-role-missing' : ''}`}
              value={row.roleId || ''}
              onChange={handleChange(index, 'roleId')}
              disabled={disabled || roles.length === 0}
              aria-label={`Role for staff address ${index + 1}`}
            >
              <option value="">No role — full access</option>
              {roles.map((role) => (
                <option key={role.id} value={role.id}>{role.name}</option>
              ))}
              {missingRole && <option value={row.roleId}>{row.roleId} (deleted)</option>}
            </select>

            <button
              type="button"
              className="pa-btn pa-btn-quiet pa-list-remove"
              onClick={() => handleRemove(index)}
              disabled={disabled}
              aria-label={`Remove staff address ${index + 1}`}
            >
              Remove
            </button>
          </div>
        );
      })}

      <button
        type="button"
        className="pa-btn pa-btn-quiet pa-list-add"
        onClick={handleAdd}
        disabled={disabled}
      >
        Add another
      </button>
    </div>
  );
}

export default PosStaffList;
